/**
 * Hook for handling interpolation in DataCloud
 * Manages interpolation parameters, processing, and results
 */
import { useState, useCallback, Dispatch } from 'react';
import { useTranslation } from 'react-i18next';
import {
  InterpolatedPoint,
  InterpolationInfo,
  DataCloudUIAction,
  CloudErrorPayload
} from '../types/dataCloud.types';
import { UploadProgress } from '@/shared/components/feedback/ProgressTracker';
import { useQuotaCheck } from '@/features/subscription/hooks/useQuotaCheck';
import DataCloudChunkedUploader from '../services/DataCloudChunkedUploader';
import { generateUploadId } from '../utils/chartHelpers';
import { cloudErrorPayloadFromError } from '../utils/cloudErrorPayload';
import { COMPUTE_HOURS_EXHAUSTED } from '@/constants/errorCodes';
import type { DecimalPrecision } from '@/types/common';

interface UseDataCloudInterpolationProps {
  file2: File | null;
  currentUploadId: string;
  setCurrentUploadId: (id: string) => void;
  decimalPrecision: DecimalPrecision;
  dispatch: Dispatch<DataCloudUIAction>;
  onShowUpgradeModal: (message: string, type: 'processing' | 'storage') => void;
}

interface UseDataCloudInterpolationReturn {
  // Parameters
  maxTimeSpan: string;
  setMaxTimeSpan: (value: string) => void;

  // Results
  interpolatedData: InterpolatedPoint[];
  interpolationInfo: InterpolationInfo | null;

  // Actions
  handleInterpolation: () => Promise<void>;
  resetInterpolation: () => void;

  // Progress
  progress: UploadProgress | null;
}

export const useDataCloudInterpolation = ({
  file2,
  currentUploadId,
  setCurrentUploadId,
  decimalPrecision,
  dispatch,
  onShowUpgradeModal
}: UseDataCloudInterpolationProps): UseDataCloudInterpolationReturn => {
  const { t } = useTranslation('dataCloud');
  const { canProcess, refreshUsage } = useQuotaCheck();

  // Parameters
  const [maxTimeSpan, setMaxTimeSpan] = useState<string>('');

  // Results
  const [interpolatedData, setInterpolatedData] = useState<InterpolatedPoint[]>([]);
  const [interpolationInfo, setInterpolationInfo] = useState<InterpolationInfo | null>(
    null
  );

  // Progress
  const [progress, setProgress] = useState<UploadProgress | null>(null);

  /**
   * Main interpolation processing handler
   */
  const handleInterpolation = useCallback(async () => {
    // Check processing quota
    const quotaCheck = await canProcess();
    if (!quotaCheck.allowed) {
      onShowUpgradeModal(
        t('errors.processingLimitReached'),
        'processing'
      );
      return;
    }

    if (!maxTimeSpan) {
      const payload: CloudErrorPayload = {
        code: undefined,
        suggestion: undefined,
        fallback: t('errors.enterMaxTimeSpan')
      };
      dispatch({ type: 'SET_ERROR', payload });
      dispatch({ type: 'SET_SUCCESS', payload: null });
      return;
    }

    if (!file2) {
      const payload: CloudErrorPayload = {
        code: undefined,
        suggestion: undefined,
        fallback: t('errors.powerFileRequired')
      };
      dispatch({ type: 'SET_ERROR', payload });
      dispatch({ type: 'SET_SUCCESS', payload: null });
      return;
    }

    dispatch({ type: 'SET_INTERPOLATING', payload: true });
    dispatch({ type: 'SET_ERROR', payload: null });
    dispatch({ type: 'SET_SUCCESS', payload: null });

    // Initialize progress
    setProgress({
      fileName: file2?.name || '',
      uploadProgress: 0,
      processingProgress: 0,
      status: 'processing',
      error: undefined
    });

    // Reset data before starting new interpolation
    setInterpolatedData([]);
    setInterpolationInfo(null);

    try {
      // Use current upload ID or generate new one
      const uploadId = currentUploadId || generateUploadId();
      if (!currentUploadId) {
        setCurrentUploadId(uploadId);
      }

      // Upload file2 for interpolation (upload bar 0-100%)
      await DataCloudChunkedUploader.uploadFile(
        file2,
        'interpolate_file',
        uploadId,
        prog =>
          setProgress({
            fileName: file2?.name || '',
            uploadProgress: prog.uploadProgress || 0,
            processingProgress: 0,
            status: prog.status,
            message: prog.message,
            error: prog.error
          }),
        refreshUsage
      );

      // Storage for all data points
      let allPoints: InterpolatedPoint[] = [];

      // Process interpolation with streaming
      const result = await DataCloudChunkedUploader.processInterpolation(
        uploadId,
        {
          max_time_span: parseInt(maxTimeSpan, 10) || 0,
          ensure_numeric: true,
          decimalPrecision
        },
        prog =>
          setProgress({
            fileName: file2?.name || '',
            uploadProgress: 100,
            processingProgress: prog.processingProgress || 0,
            status: prog.status,
            message: prog.message,
            error: prog.error,
            step: prog.step,
            eta: prog.eta,
            etaFormatted: prog.etaFormatted
          }),
        (data: unknown[]) => {
          // Accumulate streaming data
          allPoints = [...allPoints, ...(data as InterpolatedPoint[])];
        },
        refreshUsage
      );

      // Update UI with results
      setInterpolationInfo({
        addedPoints: result.addedPoints,
        totalPoints: result.totalPoints,
        removedPoints: result.removedPoints
      });

      setInterpolatedData(allPoints);
      dispatch({
        type: 'SET_SUCCESS',
        payload: { key: 'success.interpolationComplete' },
      });

      // Update progress to completed
      setProgress({
        fileName: file2?.name || '',
        uploadProgress: 100,
        processingProgress: 100,
        status: 'completed',
        error: undefined
      });
    } catch (err: unknown) {
      dispatch({ type: 'SET_SUCCESS', payload: null });

      const code = (err as { code?: string }).code;

      // Route compute_hours_exhausted to the upgrade modal (same path as the
      // pre-check canProcess() denial above). Do not dispatch SET_ERROR for
      // this code — the modal is the canonical UX for quota exhaustion.
      // Quota exhaustion is an expected user state, not a system error — skip logging.
      if (code === COMPUTE_HOURS_EXHAUSTED) {
        onShowUpgradeModal(
          t('errors.processingLimitReached'),
          'processing'
        );
      } else {
        console.error('Error during interpolation:', err);
        const payload = cloudErrorPayloadFromError(err);
        dispatch({ type: 'SET_ERROR', payload });

        // Update progress to error
        setProgress({
          fileName: file2?.name || '',
          uploadProgress: 0,
          processingProgress: 0,
          status: 'error',
          error: payload.fallback
        });
      }
    } finally {
      dispatch({ type: 'SET_INTERPOLATING', payload: false });
    }
  }, [
    file2,
    maxTimeSpan,
    decimalPrecision,
    currentUploadId,
    setCurrentUploadId,
    dispatch,
    t,
    canProcess,
    refreshUsage,
    onShowUpgradeModal
  ]);

  /**
   * Reset interpolation state
   */
  const resetInterpolation = useCallback(() => {
    setMaxTimeSpan('');
    setInterpolatedData([]);
    setInterpolationInfo(null);
    setProgress(null);
  }, []);

  return {
    // Parameters
    maxTimeSpan,
    setMaxTimeSpan,

    // Results
    interpolatedData,
    interpolationInfo,

    // Actions
    handleInterpolation,
    resetInterpolation,

    // Progress
    progress
  };
};

export default useDataCloudInterpolation;
